import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Typography, 
  Form, 
  Input, 
  Button, 
  Select, 
  Switch, 
  Row, 
  Col, 
  Divider, 
  Space, 
  message, 
  Spin,
  Alert,
  Modal,
  Tabs,
  Upload,
  DatePicker
} from 'antd'
import { 
  UserOutlined, 
  PhoneOutlined, 
  MailOutlined, 
  HomeOutlined,
  LockOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  UploadOutlined,
  FileProtectOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined
} from '@ant-design/icons'
import { customerApi } from '../services/customerApi'
import dayjs from 'dayjs'

const { Title, Text } = Typography
const { Option } = Select
const { TabPane } = Tabs

interface CustomerProfile {
  id: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  altPhone?: string
  altEmail?: string
  address?: string
  address2?: string
  city?: string
  state?: string
  zipCode?: string
  country: string
  company?: string
  customerType: string
  preferredContact: string
  canText: boolean
  canEmail: boolean
  canCall: boolean
  billingAddress?: string
  billingCity?: string
  billingState?: string
  billingZip?: string
  isTaxExempt: boolean
  taxExemptReason?: string
  taxExemptDocumentUrl?: string
  taxExemptDocumentName?: string
  taxExemptExpiration?: string
  taxExemptNumber?: string
  hasPassword: boolean
  lastLoginAt?: string
  createdAt: string
  updatedAt: string
}

export const Profile: React.FC = () => {
  const [profile, setProfile] = useState<CustomerProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [passwordModalVisible, setPasswordModalVisible] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [taxDocumentUploading, setTaxDocumentUploading] = useState(false)

  const [form] = Form.useForm()
  const [passwordForm] = Form.useForm()

  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await customerApi.getProfile()
      setProfile(response.customer)
      
      // Convert taxExemptExpiration string to dayjs object for the DatePicker
      const formValues = {
        ...response.customer,
        taxExemptExpiration: response.customer.taxExemptExpiration 
          ? dayjs(response.customer.taxExemptExpiration) 
          : null
      }
      
      form.setFieldsValue(formValues)
    } catch (err: any) {
      console.error('Failed to load profile:', err)
      setError(err.response?.data?.message || 'Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveProfile = async (values: any) => {
    try {
      setSaving(true)
      setError(null)
      
      // Convert dayjs object to ISO string for API
      const apiValues = {
        ...values,
        taxExemptExpiration: values.taxExemptExpiration 
          ? values.taxExemptExpiration.toISOString() 
          : null
      }
      
      const response = await customerApi.updateProfile(apiValues)
      setProfile(response.customer)
      setEditMode(false)
      message.success('Profile updated successfully!')
    } catch (err: any) {
      console.error('Failed to update profile:', err)
      const errorMessage = err.response?.data?.message || 'Failed to update profile'
      setError(errorMessage)
      message.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async (values: any) => {
    try {
      setPasswordLoading(true)
      
      await customerApi.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      })
      
      setPasswordModalVisible(false)
      passwordForm.resetFields()
      message.success('Password changed successfully!')
    } catch (err: any) {
      console.error('Failed to change password:', err)
      const errorMessage = err.response?.data?.message || 'Failed to change password'
      message.error(errorMessage)
    } finally {
      setPasswordLoading(false)
    }
  }

  const handleTaxDocumentUpload = async (file: any) => {
    try {
      setTaxDocumentUploading(true)
      
      const formData = new FormData()
      formData.append('file', file)
      formData.append('type', 'tax-exempt')
      
      const response = await customerApi.uploadTaxDocument(formData)
      
      message.success('Tax exempt document uploaded successfully!')
      
      // Reload profile to get updated document info
      await loadProfile()
      
      return false // Prevent default upload behavior
    } catch (err: any) {
      console.error('Failed to upload tax document:', err)
      message.error(err.response?.data?.message || 'Failed to upload document')
      return false
    } finally {
      setTaxDocumentUploading(false)
    }
  }

  const handleDeleteTaxDocument = async () => {
    try {
      await customerApi.deleteTaxDocument()
      message.success('Tax exempt document deleted successfully!')
      await loadProfile()
    } catch (err: any) {
      console.error('Failed to delete tax document:', err)
      message.error(err.response?.data?.message || 'Failed to delete document')
    }
  }

  const handleCancelEdit = () => {
    if (profile) {
      // Convert taxExemptExpiration string to dayjs object for the DatePicker
      const formValues = {
        ...profile,
        taxExemptExpiration: profile.taxExemptExpiration 
          ? dayjs(profile.taxExemptExpiration) 
          : null
      }
      form.setFieldsValue(formValues)
    }
    setEditMode(false)
    setError(null)
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!profile) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Profile Not Found"
          description="Unable to load your profile information."
          type="error"
          showIcon
          action={
            <Button onClick={loadProfile}>
              Try Again
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2}>My Profile</Title>
        <Space>
          {!editMode ? (
            <Button 
              type="primary" 
              icon={<EditOutlined />}
              onClick={() => setEditMode(true)}
            >
              Edit Profile
            </Button>
          ) : (
            <Space>
              <Button 
                icon={<CloseOutlined />}
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                icon={<SaveOutlined />}
                loading={saving}
                onClick={() => form.submit()}
              >
                Save Changes
              </Button>
            </Space>
          )}
        </Space>
      </div>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: '24px' }}
          onClose={() => setError(null)}
        />
      )}

      <Tabs defaultActiveKey="personal">
        <TabPane tab="Personal Information" key="personal">
          <Card>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSaveProfile}
              disabled={!editMode}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="First Name"
                    name="firstName"
                    rules={[{ required: true, message: 'First name is required' }]}
                  >
                    <Input prefix={<UserOutlined />} placeholder="Enter first name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Last Name"
                    name="lastName"
                    rules={[{ required: true, message: 'Last name is required' }]}
                  >
                    <Input prefix={<UserOutlined />} placeholder="Enter last name" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Email Address"
                    name="email"
                  >
                    <Input 
                      prefix={<MailOutlined />} 
                      placeholder="Email address" 
                      disabled
                      style={{ backgroundColor: '#f5f5f5' }}
                    />
                  </Form.Item>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Email cannot be changed. Contact support if needed.
                  </Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Company"
                    name="company"
                  >
                    <Input placeholder="Company name (optional)" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider>Contact Information</Divider>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Primary Phone"
                    name="phone"
                    rules={[
                      { pattern: /^[\d\s\-\(\)\+]+$/, message: 'Please enter a valid phone number' }
                    ]}
                  >
                    <Input prefix={<PhoneOutlined />} placeholder="Primary phone number" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Alternative Phone"
                    name="altPhone"
                    rules={[
                      { pattern: /^[\d\s\-\(\)\+]+$/, message: 'Please enter a valid phone number' }
                    ]}
                  >
                    <Input prefix={<PhoneOutlined />} placeholder="Alternative phone (optional)" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Alternative Email"
                    name="altEmail"
                    rules={[
                      { type: 'email', message: 'Please enter a valid email address' }
                    ]}
                  >
                    <Input prefix={<MailOutlined />} placeholder="Alternative email (optional)" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Preferred Contact Method"
                    name="preferredContact"
                  >
                    <Select placeholder="Select preferred contact method">
                      <Option value="phone">Phone</Option>
                      <Option value="email">Email</Option>
                      <Option value="text">Text Message</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="canCall"
                    valuePropName="checked"
                  >
                    <Space>
                      <Switch />
                      <Text>Allow phone calls</Text>
                    </Space>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="canText"
                    valuePropName="checked"
                  >
                    <Space>
                      <Switch />
                      <Text>Allow text messages</Text>
                    </Space>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="canEmail"
                    valuePropName="checked"
                  >
                    <Space>
                      <Switch />
                      <Text>Allow emails</Text>
                    </Space>
                  </Form.Item>
                </Col>
              </Row>

              <Divider>Address Information</Divider>

              <Form.Item
                label="Street Address"
                name="address"
              >
                <Input prefix={<HomeOutlined />} placeholder="Street address" />
              </Form.Item>

              <Form.Item
                label="Address Line 2"
                name="address2"
              >
                <Input placeholder="Apartment, suite, unit, etc. (optional)" />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="City"
                    name="city"
                  >
                    <Input placeholder="City" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="State"
                    name="state"
                  >
                    <Input placeholder="State" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="ZIP Code"
                    name="zipCode"
                    rules={[
                      { pattern: /^\d{5}(-\d{4})?$/, message: 'Please enter a valid ZIP code' }
                    ]}
                  >
                    <Input placeholder="ZIP Code" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </TabPane>

        <TabPane tab="Billing Information" key="billing">
          <Card>
            <Form
              form={form}
              layout="vertical"
              disabled={!editMode}
            >
              <Text type="secondary">
                Billing address for invoices and payments. Leave blank to use primary address.
              </Text>
              
              <Divider />

              <Form.Item
                label="Billing Address"
                name="billingAddress"
              >
                <Input prefix={<HomeOutlined />} placeholder="Billing street address" />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Billing City"
                    name="billingCity"
                  >
                    <Input placeholder="Billing city" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Billing State"
                    name="billingState"
                  >
                    <Input placeholder="Billing state" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Billing ZIP"
                    name="billingZip"
                    rules={[
                      { pattern: /^\d{5}(-\d{4})?$/, message: 'Please enter a valid ZIP code' }
                    ]}
                  >
                    <Input placeholder="Billing ZIP code" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </TabPane>

        <TabPane tab="Tax Information" key="tax">
          <Card>
            <Form
              form={form}
              layout="vertical"
              onValuesChange={(changedValues) => {
                if (changedValues.isTaxExempt !== undefined) {
                  form.setFieldsValue({ isTaxExempt: changedValues.isTaxExempt })
                }
              }}
            >
              <Alert
                message="Tax Exemption Status"
                description="If your organization is tax-exempt, please provide your tax exemption certificate or documentation."
                type="info"
                showIcon
                style={{ marginBottom: '24px' }}
              />

              <Form.Item
                label="Tax Exempt Status"
                name="isTaxExempt"
                valuePropName="checked"
              >
                <Switch 
                  checkedChildren={<CheckCircleOutlined />}
                  unCheckedChildren={<CloseCircleOutlined />}
                  disabled={!editMode}
                />
              </Form.Item>

              {profile?.isTaxExempt && (
                <>
                  <Divider>Tax Exemption Details</Divider>

                  <Form.Item
                    label="Tax Exempt Reason"
                    name="taxExemptReason"
                  >
                    <Input.TextArea 
                      rows={3}
                      placeholder="Please explain the reason for tax exemption (e.g., Non-profit organization, Government entity, etc.)" 
                      disabled={!editMode}
                    />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="Tax Exempt Certificate Number"
                        name="taxExemptNumber"
                      >
                        <Input placeholder="Certificate or ID number" disabled={!editMode} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="Expiration Date"
                        name="taxExemptExpiration"
                      >
                        <DatePicker 
                          style={{ width: '100%' }}
                          disabled={!editMode}
                          format="MMMM D, YYYY"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider>Tax Exemption Documentation</Divider>

                  <div style={{ marginBottom: '16px' }}>
                    <Text strong>Upload Tax Exempt Certificate</Text>
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '13px' }}>
                        Please upload your official tax exemption certificate or documentation (PDF, JPG, or PNG format)
                      </Text>
                    </div>
                  </div>

                  {profile?.taxExemptDocumentUrl ? (
                    <Card size="small" style={{ marginBottom: '16px', backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }}>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Space>
                          <FileProtectOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                          <div>
                            <div>
                              <Text strong>{profile.taxExemptDocumentName || 'Tax Exempt Document'}</Text>
                            </div>
                            <div>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                Uploaded on {dayjs(profile.updatedAt).format('MMMM D, YYYY')}
                              </Text>
                            </div>
                          </div>
                        </Space>
                        <Space>
                          <Button 
                            size="small" 
                            href={profile.taxExemptDocumentUrl} 
                            target="_blank"
                            icon={<EyeOutlined />}
                          >
                            View Document
                          </Button>
                          <Button 
                            size="small" 
                            danger
                            onClick={handleDeleteTaxDocument}
                          >
                            Delete
                          </Button>
                        </Space>
                      </Space>
                    </Card>
                  ) : (
                    <Alert
                      message="No tax exempt document uploaded"
                      description="Please upload your tax exemption certificate to complete your tax-exempt status."
                      type="warning"
                      showIcon
                      style={{ marginBottom: '16px' }}
                    />
                  )}

                  <Upload
                    beforeUpload={handleTaxDocumentUpload}
                    maxCount={1}
                    accept=".pdf,.jpg,.jpeg,.png"
                    showUploadList={false}
                  >
                    <Button 
                      icon={<UploadOutlined />} 
                      loading={taxDocumentUploading}
                      block
                    >
                      {profile?.taxExemptDocumentUrl ? 'Replace Document' : 'Upload Document'}
                    </Button>
                  </Upload>
                </>
              )}
            </Form>
          </Card>
        </TabPane>

        <TabPane tab="Security" key="security">
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Title level={4}>Password</Title>
                <Space direction="vertical" size="small">
                  <Text>
                    {profile.hasPassword 
                      ? `Last updated: ${profile.updatedAt ? dayjs(profile.updatedAt).format('MMMM D, YYYY') : 'Unknown'}`
                      : 'No password set up'
                    }
                  </Text>
                  <Button 
                    icon={<LockOutlined />}
                    onClick={() => setPasswordModalVisible(true)}
                    disabled={!profile.hasPassword}
                  >
                    Change Password
                  </Button>
                  {!profile.hasPassword && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Use the magic link from email to set up a password first.
                    </Text>
                  )}
                </Space>
              </div>

              <Divider />

              <div>
                <Title level={4}>Account Information</Title>
                <Space direction="vertical" size="small">
                  <div>
                    <Text strong>Customer Type: </Text>
                    <Text>{profile.customerType === 'individual' ? 'Individual' : 'Business'}</Text>
                  </div>
                  <div>
                    <Text strong>Member Since: </Text>
                    <Text>{dayjs(profile.createdAt).format('MMMM D, YYYY')}</Text>
                  </div>
                  {profile.lastLoginAt && (
                    <div>
                      <Text strong>Last Login: </Text>
                      <Text>{dayjs(profile.lastLoginAt).format('MMMM D, YYYY [at] h:mm A')}</Text>
                    </div>
                  )}
                </Space>
              </div>
            </Space>
          </Card>
        </TabPane>
      </Tabs>

      {/* Password Change Modal */}
      <Modal
        title="Change Password"
        open={passwordModalVisible}
        onCancel={() => {
          setPasswordModalVisible(false)
          passwordForm.resetFields()
        }}
        footer={null}
        width={400}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleChangePassword}
        >
          <Form.Item
            label="Current Password"
            name="currentPassword"
            rules={[{ required: true, message: 'Please enter your current password' }]}
          >
            <Input.Password placeholder="Enter current password" />
          </Form.Item>

          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[
              { required: true, message: 'Please enter a new password' },
              { min: 8, message: 'Password must be at least 8 characters long' }
            ]}
          >
            <Input.Password placeholder="Enter new password" />
          </Form.Item>

          <Form.Item
            label="Confirm New Password"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('Passwords do not match'))
                }
              })
            ]}
          >
            <Input.Password placeholder="Confirm new password" />
          </Form.Item>

          <div style={{ textAlign: 'right', marginTop: '24px' }}>
            <Space>
              <Button 
                onClick={() => {
                  setPasswordModalVisible(false)
                  passwordForm.resetFields()
                }}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={passwordLoading}
              >
                Change Password
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  )
}
